/**
 * Rep marker hand-off — tells crabplace.com which Customer Relations rep uses this browser,
 * so an order she places for a customer is recorded as taken by her and not "Order Placed
 * Online". See netlify/functions/order-taken-by.js for the whole flow.
 *
 * Runs when CPx loads signed in. The server decides who is a rep; for anyone else this is
 * one small request and nothing more. For a rep it sends the browser through
 * crabplace.com once and straight back, because that is the only way one site can leave a
 * marker for another (crabplace.com refuses to load in a hidden frame). It then stays
 * quiet for REFRESH_DAYS; the marker itself lasts over a year.
 */
import { getAccessToken, getCurrentUser } from './auth';

const STOREFRONT = 'https://crabplace.com/';
const STATE_KEY = 'cpxRepMarker';
const REFRESH_DAYS = 30;
// If a bounce never comes back confirmed, do not try again for a day: a storefront that
// is not answering must not trap her in a redirect on every page load.
const RETRY_HOURS = 24;
const DAY = 24 * 3600 * 1000;

const readState = () => { try { return JSON.parse(localStorage.getItem(STATE_KEY)) || {}; } catch { return {}; } };
const writeState = (s) => { try { localStorage.setItem(STATE_KEY, JSON.stringify(s)); } catch { /* storage off */ } };

// Called first thing on load: the storefront sends her back with ?cprepok=1.
function consumeReturn() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('cprepok')) return;
  const s = readState();
  if (s.pendingFor != null) writeState({ adminId: s.pendingFor, setAt: Date.now() });
  url.searchParams.delete('cprepok');
  window.history.replaceState(null, '', url.pathname + url.search + url.hash);
}

export async function plantRepMarker() {
  try {
    consumeReturn();
    if (window.location.hostname === 'localhost') return;
    // An installed app window would hand the trip to the system browser and lose it.
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return;
    const user = getCurrentUser();
    const token = getAccessToken();
    if (!user || !token) return;
    const adminId = String(user.adminId ?? user.admin_id ?? user.id ?? '');
    const s = readState();
    if (s.adminId === adminId && s.setAt && Date.now() - s.setAt < REFRESH_DAYS * DAY) return;
    if (s.triedAt && Date.now() - s.triedAt < RETRY_HOURS * 3600 * 1000) return;

    const res = await fetch('/.netlify/functions/order-taken-by', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action: 'mint' }),
    });
    if (!res.ok) return;
    const data = await res.json();
    if (!data.eligible || !data.enabled || !data.token) return;

    writeState({ ...s, pendingFor: adminId, triedAt: Date.now() });
    const back = window.location.origin + window.location.pathname;
    window.location.assign(`${STOREFRONT}?cprep=${encodeURIComponent(data.token)}&cpback=${encodeURIComponent(back)}`);
  } catch { /* never let this get in the way of signing in */ }
}
